/**
 * Service area — the comuni named on the site and in `areaServed` JSON-LD.
 *
 * Altitudes and coordinates were checked against it.wikipedia.org on
 * 2026-09-22 (infobox "Altitudine" and the comune's primary coordinate).
 * Distances are indicative ROAD kilometres from the Borgosesia base, kept
 * from the plan: they are rounded editorial figures, not routing output.
 */
export interface Comune {
  key: string
  name: string
  province: 'VC' | 'BI' | 'NO/VCO'
  altitude: number | null
  distanceKm: number | null
  lat: number
  lon: number
  focus?: 'base' | 'condomini' | 'lake'
  /** false = indicative value, still to be checked against Wikipedia/ISTAT; the check script reports them. */
  verified: boolean
}

export const COMUNI: readonly Comune[] = [
  // Borgosesia keeps the sede coordinate (src/sedi/verde-valsesia-2.ts geo
  // carries the same pair, and the two must stay identical).
  // Wikipedia's municipio point is 45.716944 / 8.276389 — the longitude differs
  // by ~0.0097°, ~760 m: flagged for the owner, deliberately NOT changed here.
  { key: 'borgosesia', name: 'Borgosesia',       province: 'VC',     altitude: 354,  distanceKm: 0,  lat: 45.7167, lon: 8.2667, focus: 'condomini', verified: true },
  { key: 'cellio',     name: 'Cellio con Breia', province: 'VC',     altitude: 685,  distanceKm: 9,  lat: 45.7571, lon: 8.3117, verified: true },
  { key: 'varallo',    name: 'Varallo',          province: 'VC',     altitude: 450,  distanceKm: 12, lat: 45.8138, lon: 8.2581, verified: true },
  { key: 'sostegno',   name: 'Sostegno',         province: 'BI',     altitude: 397,  distanceKm: 14, lat: 45.6529, lon: 8.2697, verified: true },
  { key: 'scopa',      name: 'Scopa',            province: 'VC',     altitude: 622,  distanceKm: 22, lat: 45.7919, lon: 8.1130, verified: true },
  { key: 'scopello',   name: 'Scopello',         province: 'VC',     altitude: 659,  distanceKm: 24, lat: 45.7667, lon: 8.1000, verified: true },
  { key: 'mollia',     name: 'Mollia',           province: 'VC',     altitude: 880,  distanceKm: 34, lat: 45.8167, lon: 8.0333, verified: true },
  // 55 km: the owner's figure (2026-09-25), was 45 from the plan.
  { key: 'alagna',     name: 'Alagna Valsesia',  province: 'VC',     altitude: 1191, distanceKm: 55, lat: 45.8539, lon: 7.9372, verified: true },
  // Not a comune: the lake as a whole. Altitude 290 m matches Wikipedia; the
  // point is the Orta San Giulio shore (where the second homes are), not the
  // lake centroid (45.8128 / 8.3956), so the map pin lands on the village.
  { key: 'orta',       name: "Lago d'Orta",      province: 'NO/VCO', altitude: 290,  distanceKm: 28, lat: 45.7980, lon: 8.4170, focus: 'lake', verified: true },
]

/** The base of operations; the zone table marks it and JSON-LD geolocates it. */
export const BASE: Comune = COMUNI[0]

/**
 * Places above the road where the squad also works — reached by lift or on
 * foot, each inside a comune already listed. They are not comuni, so they
 * stay out of `COMUNI` (JSON-LD `areaServed` types those as City, and the
 * zone table and map are keyed on road distance and a municipal point) and
 * appear only where altitude is the point: the manifesto's valley profile,
 * both zone maps (a hollow ring at the end of a lift leg — the home's compact
 * one too, owner 2026-09-25) and the Zone page's table — not JSON-LD.
 */
export interface HighPlace {
  key: string
  name: string
  /** The `COMUNI` entry it sits in; the profile draws it after that comune, the map hangs its lift leg from it. */
  comuneKey: string
  altitude: number
  lat: number
  lon: number
  verified: boolean
}

export const HIGH_PLACES: readonly HighPlace[] = [
  // Rifugio Città di Mortara at Alpe Seewji, 13021 Alagna Valsesia (the
  // hut's own site, grandehalte.it), above the Pianalunga lift station.
  // 1 945 m and 45.863916 N / 7.909441 E per peakvisor.com, 2026-09-24 —
  // one source, so `verified: false` until the owner confirms (the build
  // warns, like an unverified comune).
  { key: 'grande-halte', name: 'Grande Halte', comuneKey: 'alagna', altitude: 1945, lat: 45.8639, lon: 7.9094, verified: false },
]
